/**
 * AccessControlPage — per-agent grant admin (route: /admin/access-control).
 *
 * Backed by CMHKAgentPlatformPermissionManager through the Gateway `/permission/*`
 * relay (see permissionManagerClient). One table lists every grant on the
 * selected agent: who (team / person / POSIX group / LDAP group), what they may
 * do (Use = invoke, Manage = invoke + manage), who granted it and when. Add
 * creates or replaces a grant (PutGrant is an upsert), Remove revokes it.
 *
 * Authorization is per resource and server-side: ListGrants / PutGrant /
 * DeleteGrant require a `manage` grant on the agent (or platform-admin Team
 * membership) and answer 403 otherwise, which this page surfaces as-is. There is
 * deliberately no client-side LDAP gate any more — an agent owner without
 * rocky-admin must be able to manage their own agent.
 *
 * Agent list = the static AGENT_REGISTRY (bootstrap: a brand-new agent has no
 * grants yet, so nothing server-side can list it) ∪ every agent the caller holds
 * `manage` on (ListAuthorizedResources), so owners see agents outside the registry.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '@amzn/awsui-components-react/polaris/header';
import Table, { TableProps } from '@amzn/awsui-components-react/polaris/table';
import Select, { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import Button from '@amzn/awsui-components-react/polaris/button';
import Box from '@amzn/awsui-components-react/polaris/box';
import Icon, { IconProps } from '@amzn/awsui-components-react/polaris/icon';
import Badge from '@amzn/awsui-components-react/polaris/badge';
import Link from '@amzn/awsui-components-react/polaris/link';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import RadioGroup from '@amzn/awsui-components-react/polaris/radio-group';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import BreadcrumbGroup from '@amzn/awsui-components-react/polaris/breadcrumb-group';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import TextFilter from '@amzn/awsui-components-react/polaris/text-filter';
import CollectionPreferences, {
  CollectionPreferencesProps,
} from '@amzn/awsui-components-react/polaris/collection-preferences';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { AGENT_REGISTRY, MANAGEABLE_AGENTS } from '../../context/AgentContext';
import {
  permissionManagerClient,
  PermissionApiError,
  type Action,
  type Grant,
  type PrincipalType,
} from '../../client/permissionManagerClient';
import './AccessControlPage.css';

const RESOURCE_TYPE = 'agent' as const;

// ── Principal kinds ───────────────────────────────────────────────────────────

/** Label, id label and id hint come from settings:access.kinds.<value>. */
interface PrincipalKind {
  value: PrincipalType;
  icon: IconProps.Name;
  placeholder: string;
  /** Teams carry an opaque Abacus id, so a display name is worth asking for. */
  askName: boolean;
}

const PRINCIPAL_KINDS: PrincipalKind[] = [
  {
    value: 'user',
    icon: 'user-profile',
    placeholder: 'jiananj',
    askName: false,
  },
  {
    value: 'team',
    icon: 'group',
    placeholder: 'amzn1.abacus.team.abc123',
    askName: true,
  },
  {
    value: 'ldap',
    icon: 'group',
    placeholder: 'team-dat-dataeng',
    askName: false,
  },
  {
    value: 'posix',
    icon: 'group',
    placeholder: 'dat-engineers',
    askName: false,
  },
];

const kindOf = (t: PrincipalType): PrincipalKind =>
  PRINCIPAL_KINDS.find((k) => k.value === t) ?? PRINCIPAL_KINDS[0];

// Options are built at render (see the component) so their labels follow the locale.
const ACCESS_FILTER_VALUES = [
  { value: 'all', labelKey: 'access.anyAccess' },
  { value: 'manage', labelKey: 'access.canManage' },
  { value: 'invoke', labelKey: 'access.useOnly' },
] as const;

/** Access level a grant confers: `manage` implies `invoke` server-side. */
const canManage = (g: Grant) => g.actions.includes('manage');
const actionsFor = (level: Action): Action[] => (level === 'manage' ? ['invoke', 'manage'] : ['invoke']);

const grantKey = (g: { principalType: PrincipalType; principalId: string }) =>
  `${g.principalType}#${g.principalId}`;

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

const PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizeOption[] = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
];

const REGISTRY_OPTIONS: SelectProps.Option[] = AGENT_REGISTRY.map((a) => ({
  value: a.id,
  label: a.label,
}));

// Manage-only agents: not user-selectable runtime agents (absent from
// AGENT_REGISTRY), but administrable here. Listed up front so they appear with a
// friendly label even before their first grant exists.
const MANAGEABLE_OPTIONS: SelectProps.Option[] = MANAGEABLE_AGENTS.map((a) => ({
  value: a.id,
  label: a.label,
}));

/** Every agent selectable in the dropdown by default, before the server augment. */
const BASE_OPTIONS: SelectProps.Option[] = [...REGISTRY_OPTIONS, ...MANAGEABLE_OPTIONS];

/** Ids already shown with a known label, so the server augment never re-adds them. */
const KNOWN_AGENT_IDS = new Set<string>([
  ...AGENT_REGISTRY.map((a) => a.id),
  ...MANAGEABLE_AGENTS.map((a) => a.id),
]);

/** A server message is shown as-is; anything else is a key translated at render. */
type PageError = { message: string } | { key: 'forbidden' | 'unreachable' | 'saveFailed' | 'revokeFailed' };

const describeError = (e: unknown, fallback: 'unreachable' | 'saveFailed' | 'revokeFailed'): PageError => {
  if (e instanceof PermissionApiError) {
    if (e.statusCode === 403) return { key: 'forbidden' };
    return { message: e.message };
  }
  return { key: fallback };
};

export default function AccessControlPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const errorText = (e: PageError) => ('message' in e ? e.message : t(`access.${e.key}`));

  const kindOptions: SelectProps.Option[] = useMemo(
    () => PRINCIPAL_KINDS.map((k) => ({ value: k.value, label: t(`access.kinds.${k.value}.label`) })),
    [t],
  );
  const typeFilterOptions: SelectProps.Option[] = useMemo(
    () => [{ value: 'any', label: t('access.anyType') }, ...kindOptions],
    [t, kindOptions],
  );
  const accessFilterOptions: SelectProps.Option[] = useMemo(
    () => ACCESS_FILTER_VALUES.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t],
  );

  const [agentOptions, setAgentOptions] = useState<SelectProps.Option[]>(BASE_OPTIONS);
  const [agent, setAgent] = useState<SelectProps.Option>(BASE_OPTIONS[0]);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<PageError | null>(null);
  // Success banner after a save. Add vs edit is fixed at save time because
  // `editing` is reset the next time the modal opens; `note` is the server's text.
  const [notice, setNotice] = useState<{ wasEdit: boolean; note?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // Filters keep only the value; the option (with its label) is derived each render.
  const [typeFilterValue, setTypeFilterValue] = useState('any');
  const [accessFilterValue, setAccessFilterValue] = useState('all');
  const typeFilter = typeFilterOptions.find((o) => o.value === typeFilterValue) ?? typeFilterOptions[0];
  const accessFilter = accessFilterOptions.find((o) => o.value === accessFilterValue) ?? accessFilterOptions[0];
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
  });

  // Add / edit modal. `editing` holds the grant being replaced (principal locked).
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Grant | null>(null);
  const [formType, setFormType] = useState<PrincipalType>('user');
  const [formId, setFormId] = useState('');
  const [formName, setFormName] = useState('');
  const [formLevel, setFormLevel] = useState<Action>('invoke');
  const [formError, setFormError] = useState<PageError | null>(null);

  const agentId = agent.value as string;

  // Agents the caller can manage that aren't already in the dropdown. Best
  // effort: a failure here only means the dropdown stays at BASE_OPTIONS
  // (registry + manage-only agents).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const extra: SelectProps.Option[] = [];
        let nextToken: string | undefined;
        do {
          const page = await permissionManagerClient.listAuthorizedResources({
            resourceType: RESOURCE_TYPE,
            action: 'manage',
            maxResults: 500,
            nextToken,
          });
          for (const r of page.resources) {
            if (!KNOWN_AGENT_IDS.has(r.resourceId)) {
              extra.push({ value: r.resourceId, label: r.resourceId });
            }
          }
          nextToken = page.nextToken;
        } while (nextToken);
        if (!cancelled && extra.length > 0) {
          extra.sort((a, b) => (a.value ?? '').localeCompare(b.value ?? ''));
          setAgentOptions([...BASE_OPTIONS, ...extra]);
        }
      } catch {
        /* dropdown stays at the registry */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await permissionManagerClient.listAllGrants({
        resourceType: RESOURCE_TYPE,
        resourceId: agentId,
      });
      setGrants(all);
    } catch (e) {
      setGrants([]);
      setError(describeError(e, 'unreachable'));
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setFormType('user');
    setFormId('');
    setFormName('');
    setFormLevel('invoke');
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (g: Grant) => {
    setEditing(g);
    setFormType(g.principalType);
    setFormId(g.principalId);
    setFormName(g.principalName !== g.principalId ? g.principalName : '');
    setFormLevel(canManage(g) ? 'manage' : 'invoke');
    setFormError(null);
    setModalOpen(true);
  };

  const doSave = async () => {
    const principalId = formId.trim();
    if (!principalId || busy) return;
    setBusy(true);
    setFormError(null);
    const wasEdit = editing !== null;
    try {
      const { propagationNote } = await permissionManagerClient.putGrant({
        resourceType: RESOURCE_TYPE,
        resourceId: agentId,
        principalType: formType,
        principalId,
        actions: actionsFor(formLevel),
        principalName: formName.trim() || undefined,
      });
      setModalOpen(false);
      setNotice({ wasEdit, note: propagationNote || undefined });
      await load();
    } catch (e) {
      setFormError(describeError(e, 'saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  // Revoke one or many grants. Runs the deletes sequentially so a mid-batch
  // failure surfaces without leaving half the UI stale, then reloads once.
  const doRevoke = async (targets: readonly Grant[]) => {
    if (busy || targets.length === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      for (const g of targets) {
        await permissionManagerClient.deleteGrant({
          resourceType: RESOURCE_TYPE,
          resourceId: agentId,
          principalType: g.principalType,
          principalId: g.principalId,
        });
      }
      await load();
    } catch (e) {
      setError(describeError(e, 'revokeFailed'));
    } finally {
      setBusy(false);
    }
  };

  // Type / access dropdowns are applied before the collection so search, sort
  // and pagination all operate on the already-narrowed set.
  const filteredGrants = useMemo(
    () =>
      grants.filter((g) => {
        const typeOk = typeFilter.value === 'any' || g.principalType === typeFilter.value;
        const accessOk =
          accessFilter.value === 'all' ||
          (accessFilter.value === 'manage' ? canManage(g) : !canManage(g));
        return typeOk && accessOk;
      }),
    [grants, typeFilter, accessFilter],
  );

  const columns: TableProps.ColumnDefinition<Grant>[] = [
    {
      id: 'name',
      header: t('access.columns.name'),
      cell: (g) => (
        <span className="ac-name">
          <Icon name={kindOf(g.principalType).icon} />
          <span>{g.principalName}</span>
          {g.principalName !== g.principalId && <span className="ac-muted">({g.principalId})</span>}
        </span>
      ),
      sortingField: 'principalName',
      isRowHeader: true,
    },
    {
      id: 'type',
      header: t('access.columns.type'),
      cell: (g) => t(`access.kinds.${g.principalType}.label`),
      sortingField: 'principalType',
    },
    {
      id: 'access',
      header: t('access.columns.access'),
      cell: (g) => (canManage(g) ? <Badge color="blue">{t('access.manage')}</Badge> : <Badge>{t('access.use')}</Badge>),
      sortingComparator: (a, b) => Number(canManage(a)) - Number(canManage(b)),
    },
    {
      id: 'grantedBy',
      header: t('access.columns.grantedBy'),
      cell: (g) => g.grantedBy || '—',
      sortingField: 'grantedBy',
    },
    {
      id: 'grantedAt',
      header: t('access.columns.grantedAt'),
      cell: (g) => formatDate(g.grantedAt),
      sortingField: 'grantedAt',
    },
    {
      id: 'expiresAt',
      header: t('access.columns.expiresAt'),
      cell: (g) => formatDate(g.expiresAt),
    },
    {
      id: 'action',
      header: t('access.columns.action'),
      cell: (g) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="inline-link" disabled={busy} onClick={() => openEdit(g)}>
            {t('access.edit')}
          </Button>
          <Button variant="inline-link" disabled={busy} onClick={() => doRevoke([g])}>
            {t('access.remove')}
          </Button>
        </SpaceBetween>
      ),
    },
  ];

  const { items, filteredItemsCount, collectionProps, filterProps, paginationProps, actions } =
    useCollection(filteredGrants, {
      filtering: {
        empty: (
          <Box textAlign="center" color="inherit" padding="l">
            <SpaceBetween size="m">
              <b>{t('access.emptyTitle')}</b>
              <Box color="text-body-secondary">
                {t('access.emptyBody')}
              </Box>
            </SpaceBetween>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit" padding="l">
            <b>{t('access.noMatchTitle')}</b>
            <Box color="text-body-secondary">{t('access.noMatchBody')}</Box>
          </Box>
        ),
      },
      pagination: { pageSize: preferences.pageSize ?? 10 },
      sorting: { defaultState: { sortingColumn: columns[0] } },
      selection: {},
    });

  const selected = collectionProps.selectedItems ?? [];
  const formKind = kindOf(formType);

  return (
    <div className="ac-page">
      <BreadcrumbGroup
        items={[
          { text: t('access.breadcrumbHome'), href: '/' },
          { text: t('access.breadcrumbRbms'), href: '/admin-rbms' },
          { text: t('access.breadcrumbAccess'), href: '/admin/access-control' },
        ]}
        onFollow={(e) => {
          e.preventDefault();
          navigate(e.detail.href);
        }}
      />

      <div className="ac-titlerow">
        <h1 className="ac-title">{t('access.title', { agent: agent.label })}</h1>
        <Select
          selectedOption={agent}
          onChange={({ detail }) => {
            setNotice(null);
            // useCollection keeps selectedItems across item reloads; a selection
            // made on the previous agent must never feed a bulk Remove on this one.
            actions.setSelectedItems([]);
            setAgent(detail.selectedOption);
          }}
          options={agentOptions}
          disabled={busy}
          ariaLabel={t('access.agentLabel')}
        />
      </div>

      <SpaceBetween size="l">
        {/* `''` rather than null when there is no error: SpaceBetween keeps an empty slot
            for it, which is the page's existing spacing. */}
        {error ? (
          <Alert type="error" header={t('access.errorHeader')} dismissible onDismiss={() => setError(null)}>
            {errorText(error)}
          </Alert>
        ) : ''}
        {notice && (
          <Alert
            type="success"
            header={notice.wasEdit ? t('access.updatedHeader') : t('access.grantedHeader')}
            dismissible
            onDismiss={() => setNotice(null)}
          >
            {notice.note ?? (notice.wasEdit ? t('access.updatedBody') : t('access.savedBody'))}
          </Alert>
        )}

        <Table
          {...collectionProps}
          variant="container"
          columnDefinitions={columns}
          items={items}
          loading={loading}
          loadingText={t('access.loading')}
          trackBy={grantKey}
          selectionType="multi"
          resizableColumns
          header={
            <Header
              counter={`(${filteredGrants.length})`}
              description={t('access.tableDescription')}
              info={
                <Popover
                  header={t('access.infoHeader')}
                  content={t('access.infoBody')}
                  triggerType="text"
                >
                  <Link variant="info">{t('access.info')}</Link>
                </Popover>
              }
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {selected.length > 0 && (
                    <Button
                      iconName="remove"
                      disabled={busy}
                      onClick={async () => {
                        await doRevoke(selected);
                        actions.setSelectedItems([]);
                      }}
                    >
                      {t('access.removeSelected', { n: selected.length })}
                    </Button>
                  )}
                  <Button variant="primary" iconName="add-plus" disabled={busy} onClick={openAdd}>
                    {t('access.addGrant')}
                  </Button>
                </SpaceBetween>
              }
            >
              {t('access.tableTitle')}
            </Header>
          }
          filter={
            <div className="ac-toolbar">
              <div className="ac-toolbar__search">
                <TextFilter
                  {...filterProps}
                  filteringPlaceholder={t('access.search')}
                  countText={t('access.matches', { n: filteredItemsCount ?? 0 })}
                />
              </div>
              <Select
                selectedOption={typeFilter}
                onChange={({ detail }) => setTypeFilterValue(detail.selectedOption.value ?? 'any')}
                options={typeFilterOptions}
                ariaLabel={t('access.filterType')}
              />
              <Select
                selectedOption={accessFilter}
                onChange={({ detail }) => setAccessFilterValue(detail.selectedOption.value ?? 'all')}
                options={accessFilterOptions}
                ariaLabel={t('access.filterAccess')}
              />
            </div>
          }
          pagination={<Pagination {...paginationProps} />}
          preferences={
            <CollectionPreferences
              title={t('access.preferences')}
              confirmLabel={t('access.confirm')}
              cancelLabel={t('access.cancel')}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={{ title: t('access.pageSize'), options: PAGE_SIZE_OPTIONS }}
            />
          }
        />
      </SpaceBetween>

      <Modal
        visible={modalOpen}
        onDismiss={() => setModalOpen(false)}
        header={editing ? t('access.editTitle', { name: editing.principalName }) : t('access.addGrant')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setModalOpen(false)} disabled={busy}>
                {t('access.cancel')}
              </Button>
              <Button variant="primary" onClick={doSave} loading={busy} disabled={!formId.trim()}>
                {editing ? t('access.save') : t('access.grantAccess')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {/* `''` keeps SpaceBetween's existing empty slot, as above. */}
          {formError ? (
            <Alert type="error" header={t('access.errorHeader')}>
              {errorText(formError)}
            </Alert>
          ) : ''}
          <FormField label={t('access.formType')}>
            <Select
              selectedOption={kindOptions.find((o) => o.value === formType) ?? kindOptions[0]}
                onChange={({ detail }) => {
                setFormType(detail.selectedOption.value as PrincipalType);
                // The display-name field only renders for some kinds; drop a value
                // typed under a previous kind so it is never sent with the grant.
                setFormName('');
              }}
              options={kindOptions}
              disabled={editing !== null}
            />
          </FormField>
          <FormField label={t(`access.kinds.${formKind.value}.idLabel`)} description={t(`access.kinds.${formKind.value}.idHint`)}>
            <Input
              value={formId}
              onChange={({ detail }) => setFormId(detail.value)}
              placeholder={formKind.placeholder}
              disabled={editing !== null}
              onKeyDown={({ detail }) => {
                if (detail.key === 'Enter') doSave();
              }}
            />
          </FormField>
          {formKind.askName && (
            <FormField label={t('access.displayName')} description={t('access.displayNameHint')}>
              <Input
                value={formName}
                onChange={({ detail }) => setFormName(detail.value)}
                placeholder="DAT Data Engineering"
              />
            </FormField>
          )}
          <FormField label={t('access.formAccess')}>
            <RadioGroup
              value={formLevel}
              onChange={({ detail }) => setFormLevel(detail.value as Action)}
              items={[
                { value: 'invoke', label: t('access.use'), description: t('access.useDescription') },
                {
                  value: 'manage',
                  label: t('access.manage'),
                  description: t('access.manageDescription'),
                },
              ]}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </div>
  );
}
